import type { Api } from "../../../services/api";
import type { ForYouFeedItem } from "../../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import type { SearchArticleItem } from "../../../features/search/hooks/useSearchArticlesQuery";
import type {
  ArticleDetail,
  RelatedArticleCard,
} from "../../../features/article/hooks/useArticleData";
import {
  AnalyticsItemCategory,
  AnalyticsItemList,
  AnalyticsItemVariant,
} from "../analytics.types";
import { gaPushEvent } from "../base.analytics";
import type { QuestionOrigin } from "../../../domains/gpt/hooks/useGptChat";
import {
  AnalyticsItemReturnData,
  BaseEvent,
  gaEventSelectItem,
  gaEventViewItem,
  gaEventViewItemList,
} from "../common.analytics";
import type { CongressAttribution } from "../../../domains/congress/congressAttribution";
import type { ConversationEngine } from "../../../domains/assistant/conversationEngine";

enum Event {
  ARTICLE_LIKE = "article_like",
  ARTICLE_SAVE = "article_save",
  ARTICLE_SHARE = "article_share",
  ARTICLE_CONSUMED = "article_consumed",
  ARTICLE_CHAT_QUESTION = "article_chat_question",
  VISUAL_ABSTRACT_CTA_SHOWN = "visual_abstract_cta_shown",
  VISUAL_ABSTRACT_REQUESTED = "visual_abstract_requested",
  VISUAL_ABSTRACT_UNAVAILABLE = "visual_abstract_unavailable",
  VISUAL_ABSTRACT_GENERATION_SUCCEEDED = "visual_abstract_generation_succeeded",
  VISUAL_ABSTRACT_GENERATION_FAILED = "visual_abstract_generation_failed",
  VISUAL_ABSTRACT_RENDERED = "visual_abstract_rendered",
  VISUAL_ABSTRACT_SHARED = "visual_abstract_shared",
  VISUAL_ABSTRACT_SHARE_REVOKED = "visual_abstract_share_revoked",
  VISUAL_ABSTRACT_PUBLIC_OPENED = "visual_abstract_public_open",
  VISUAL_ABSTRACT_PUBLIC_SHARED = "visual_abstract_public_share",
  VISUAL_ABSTRACT_ARTICLE_CLICKED = "visual_abstract_article_click",
  VISUAL_ABSTRACT_SIGNUP_CLICKED = "visual_abstract_signup_click",
  VISUAL_ABSTRACT_IMAGE_SHARED = "visual_abstract_image_share",
  VISUAL_ABSTRACT_OPENED_FROM_LIBRARY = "visual_abstract_opened_from_library",
  VISUAL_ABSTRACT_REPORT_SUBMITTED = "visual_abstract_report_submitted",
  VISUAL_ABSTRACT_USEFULNESS_ANSWERED = "visual_abstract_usefulness_answered",
}

/**
 * The dimensions every artifact-scoped visual-abstract event carries, derived
 * from the API contract rather than redeclared: `language` and `family` are
 * unions there, and a local `string` would let a typo through and silently
 * fragment a dimension in GA4.
 *
 * The share TOKEN is deliberately absent and must never be added: the public
 * surfaces are reached through a capability URL, and a token in an analytics
 * payload is the capability itself leaving the product
 * (`.knowledge/mixpanel-capability-url-leak.md`). Events name the artifact by
 * its id, which grants nothing.
 */
type VisualAbstractDimensions = {
  visualAbstractId: string;
  roomId: string | null;
  language: Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"]["language"];
  family: Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"]["family"];
  // The paper's design — the board's `article_type`. Required, never defaulted:
  // it is the dimension §B splits the success rate on and §F is entirely built
  // from, and a call site that could omit it would silently report a whole
  // surface as untyped. Nullable only for artifacts predating the column.
  sourceType: Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"]["sourceType"];
  generatorVersion: string;
};

function visualAbstractParams(dimensions: VisualAbstractDimensions) {
  return {
    visual_abstract_id: dimensions.visualAbstractId,
    room_id: dimensions.roomId,
    language: dimensions.language,
    family: dimensions.family,
    source_type: dimensions.sourceType,
    generator_version: dimensions.generatorVersion,
  };
}

type VisualAbstractBlockers = Extract<
  Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"],
  { status: "ready" }
>["blockers"];

type VisualAbstractFailureCode = Extract<
  Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"],
  { status: "failed" }
>["failure"]["code"];

/**
 * WHERE the offer was made. Four client surfaces: the conversation's
 * transcript card (`cta`, which has a month of history under that name and is
 * not renamed for it), the article page's composer pill (`article_page`), and
 * the conversation's composer band (`band`) — the proposal that takes the
 * composer over, in the Room proposal's slot. `library` is the confirmation
 * sheet of the "New visual abstract" screen, opened from the library's "+".
 *
 * The other value the board reads, `chat`, is emitted server-side by JuisciGPT's
 * `generateVisualAbstract` tool and never passes through this file, so it is
 * deliberately absent from the union: a client call site could not honestly
 * claim it.
 */
type VisualAbstractCtaTrigger = "cta" | "article_page" | "band" | "library";

/**
 * The CTA being SEEN — the denominator the funnel had no way to compute, since
 * it used to start at `requested`. `affordance` says which state the card was
 * offering, so the click-through of "Generate" is not diluted by the renders
 * where the button was not on screen at all.
 *
 * `trigger` is what makes the two surfaces comparable. Without it the pill's
 * impressions and the card's land in one undifferentiated total, and neither
 * surface's conversion can be read.
 */
export function gaEventVisualAbstractCtaShown(payload: {
  articleId: string;
  roomId: string | null;
  affordance: "generate" | "generating" | "ready" | "failed";
  trigger: VisualAbstractCtaTrigger;
}): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_CTA_SHOWN, {
    article_id: payload.articleId,
    room_id: payload.roomId,
    affordance: payload.affordance,
    trigger: payload.trigger,
  });
}

/**
 * The papers whose refusal this tab has already reported.
 *
 * Two surfaces resolve eligibility — the article page's pill and the
 * conversation's card — and a reader who asks a question walks through both, so
 * an ungated emit would count one refused paper twice and inflate the
 * eligibility gap by exactly the readers who engaged most. The guard lives
 * beside the event rather than in either caller because "one refusal per paper
 * per visit" is a property of the event, not of the screen that noticed it.
 */
const reportedUnavailableArticleIds = new Set<string>();

/**
 * A paper the feature REFUSES, reached by a reader who could have asked for a
 * poster — the board's "out-of-scope attempts, count them" (§F).
 *
 * It cannot be read off any other event: an ineligible paper renders no card at
 * all, so it produces no impression, no request, and no row. Nor is it the same
 * question as corpus coverage, which SQL answers over every article: this counts
 * refusals WEIGHTED BY DEMAND — the designs readers actually bring to the
 * feature and are turned away from.
 *
 * `reason` is the API's own eligibility verdict, not a local re-derivation, so
 * a rule added to `evaluateVisualAbstractEligibility` shows up here by itself.
 *
 * Reported ONCE per article per tab (see `reportedUnavailableArticleIds`).
 */
export function gaEventVisualAbstractUnavailable(payload: {
  articleId: string;
  roomId: string | null;
  reason: Extract<
    Api["get"]["/webapp/private/articles/:id/visual-abstracts"]["output"]["body"]["eligibility"],
    { state: "ineligible" }
  >["reason"];
}): void {
  if (reportedUnavailableArticleIds.has(payload.articleId)) return;
  reportedUnavailableArticleIds.add(payload.articleId);
  gaPushEvent(Event.VISUAL_ABSTRACT_UNAVAILABLE, {
    article_id: payload.articleId,
    room_id: payload.roomId,
    reason: payload.reason,
  });
}

/**
 * A generation actually starting, from the CTA the user tapped.
 *
 * `trigger` is passed rather than emitted: two client surfaces reach this
 * function, and a function that names the surface itself would report the
 * article page's generations as the conversation card's. The third trigger —
 * JuisciGPT's `generateVisualAbstract` tool — never touches the client at all,
 * so its `trigger: "chat"` twin is emitted server-side from the tool itself
 * (`apps/api/.../chat-with-paper.ts`). All three must keep the SAME event name
 * and property keys: the board compares them, and a dimension that exists on
 * only one path reports the others as untriggered.
 */
export function gaEventVisualAbstractRequested(payload: {
  articleId: string;
  roomId: string | null;
  sourceType: Api["post"]["/webapp/private/articles/:id/visual-abstracts"]["output"]["body"]["sourceType"];
  trigger: VisualAbstractCtaTrigger;
}): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_REQUESTED, {
    trigger: payload.trigger,
    article_id: payload.articleId,
    room_id: payload.roomId,
    source_type: payload.sourceType,
  });
}

/**
 * Generation reaching a terminal state, observed by the client that was
 * watching it. `latency_ms` is measured from the artifact's `createdAt` to the
 * poll that saw the outcome, so it carries the polling granularity (≤3s) on
 * top of the true server duration — it measures WAIT AS EXPERIENCED, which is
 * the funnel's question. The server-side duration is derivable in SQL from
 * `created_at`/`updated_at` if the pipeline itself ever needs timing.
 */
export function gaEventVisualAbstractGenerationSucceeded(
  payload: VisualAbstractDimensions & {
    articleId: string;
    latencyMs: number;
    blockers: VisualAbstractBlockers;
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_GENERATION_SUCCEEDED, {
    ...visualAbstractParams(payload),
    article_id: payload.articleId,
    latency_ms: payload.latencyMs,
    blockers_count: payload.blockers.length,
    blocker_codes: payload.blockers.map((blocker) => blocker.code).join(","),
  });
}

export function gaEventVisualAbstractGenerationFailed(
  payload: VisualAbstractDimensions & {
    articleId: string;
    latencyMs: number;
    failureCode: VisualAbstractFailureCode;
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_GENERATION_FAILED, {
    ...visualAbstractParams(payload),
    article_id: payload.articleId,
    latency_ms: payload.latencyMs,
    failure_code: payload.failureCode,
  });
}

/**
 * The owner's full-screen preview painting the poster — the step between a
 * successful generation and a share. Deliberately NOT emitted on the public
 * page: `visual_abstract_public_open` already fires there, and only once the
 * poster has rendered, so a second event would double-count the same paint.
 */
export function gaEventVisualAbstractRendered(
  payload: VisualAbstractDimensions & { blockers: VisualAbstractBlockers }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_RENDERED, {
    ...visualAbstractParams(payload),
    blockers_count: payload.blockers.length,
    blocker_codes: payload.blockers.map((blocker) => blocker.code).join(","),
  });
}

/**
 * The owner minting the capability link. `channel` names the in-app affordance
 * that did it — the only share channel the product can actually observe. Where
 * the link travels next belongs to the OS share sheet, which reports nothing
 * back, so no event here pretends to know it.
 */
export function gaEventVisualAbstractShared(
  payload: VisualAbstractDimensions & {
    channel: "share_sheet" | "copy_link";
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_SHARED, {
    ...visualAbstractParams(payload),
    channel: payload.channel,
  });
}

export function gaEventVisualAbstractShareRevoked(
  payload: VisualAbstractDimensions
): void {
  gaPushEvent(
    Event.VISUAL_ABSTRACT_SHARE_REVOKED,
    visualAbstractParams(payload)
  );
}

/**
 * The owner exporting the poster as a PNG from the preview — distinct from
 * VISUAL_ABSTRACT_SHARED, which is the owner minting the capability link.
 *
 * It carries `channel` like its sibling because the two collapse into a single
 * `visual_abstract_shared` on the Mixpanel side, where the board's share rate
 * is read: the literal lives here, in typed code, rather than in the event map
 * where every property is `unknown`.
 */
export function gaEventVisualAbstractImageShared(
  payload: VisualAbstractDimensions
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_IMAGE_SHARED, {
    ...visualAbstractParams(payload),
    channel: "image",
  });
}

/**
 * A poster re-opened from the Library — the re-consumption signal the board's
 * retention section reads. It cannot be derived from the preview's
 * `VISUAL_ABSTRACT_RENDERED`, which fires on every paint whatever route led to
 * it: without this event a deliberate return to an old poster is
 * indistinguishable from the paint that follows a generation.
 */
export function gaEventVisualAbstractOpenedFromLibrary(
  payload: VisualAbstractDimensions
): void {
  gaPushEvent(
    Event.VISUAL_ABSTRACT_OPENED_FROM_LIBRARY,
    visualAbstractParams(payload)
  );
}

export function gaEventVisualAbstractPublicOpened(
  payload: VisualAbstractDimensions & {
    brandSource: "juisci" | "room";
    articleSlug: string;
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_PUBLIC_OPENED, {
    ...visualAbstractParams(payload),
    brand_source: payload.brandSource,
    article_slug: payload.articleSlug,
  });
}

/**
 * A viewer re-sharing a public artifact — distinct from
 * VISUAL_ABSTRACT_SHARED, which is the owner minting the capability link.
 */
export function gaEventVisualAbstractPublicShared(
  payload: VisualAbstractDimensions & {
    brandSource: "juisci" | "room";
    articleSlug: string;
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_PUBLIC_SHARED, {
    ...visualAbstractParams(payload),
    brand_source: payload.brandSource,
    article_slug: payload.articleSlug,
  });
}

export function gaEventVisualAbstractArticleClicked(
  payload: VisualAbstractDimensions & {
    brandSource: "juisci" | "room";
    articleSlug: string;
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_ARTICLE_CLICKED, {
    ...visualAbstractParams(payload),
    brand_source: payload.brandSource,
    article_slug: payload.articleSlug,
  });
}

/**
 * A stranger on a shared poster asking for an account.
 *
 * The acquisition counterpart of the article click, and reported the same way —
 * a `dataLayer` push from an anchor's `onClick`, immediately before the browser
 * leaves for `/signup`. No `article_slug`: the reader is not going to a paper.
 *
 * This event is the CLICK, never the signup. The signup itself is attributed by
 * Mixpanel's first-touch `$initial_utm_source`, captured when the visitor lands
 * in the app shell — the public poster cannot run Mixpanel at all
 * (`.knowledge/mixpanel-capability-url-leak.md`), so the two halves of the
 * funnel are deliberately reported by different tools.
 */
export function gaEventVisualAbstractSignupClicked(
  payload: VisualAbstractDimensions & { brandSource: "juisci" | "room" }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_SIGNUP_CLICKED, {
    ...visualAbstractParams(payload),
    brand_source: payload.brandSource,
  });
}

/**
 * An inaccuracy report reaching the API. The artifact id comes from the API
 * response, not from the page's route: the report page is entered through a
 * capability URL and holds only the share token, which must never travel to
 * analytics.
 */
export function gaEventVisualAbstractReportSubmitted(payload: {
  visualAbstractId: string;
  sourceType: Api["post"]["/webapp/private/visual-abstracts/:id/reports"]["output"]["body"]["sourceType"];
  category: Api["post"]["/webapp/private/visual-abstracts/:id/reports"]["input"]["json"]["category"];
}): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_REPORT_SUBMITTED, {
    visual_abstract_id: payload.visualAbstractId,
    source_type: payload.sourceType,
    category: payload.category,
  });
}

export function gaEventVisualAbstractUsefulnessAnswered(
  payload: VisualAbstractDimensions & {
    surface: "preview" | "public";
    useful: boolean;
  }
): void {
  gaPushEvent(Event.VISUAL_ABSTRACT_USEFULNESS_ANSWERED, {
    ...visualAbstractParams(payload),
    surface: payload.surface,
    useful: payload.useful,
  });
}

type RoomArticleItem = Extract<
  Api["get"]["/webapp/private/content/last-in-user-room"]["output"]["body"][number],
  { content_format: "Article" }
>;
type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

type ArticleAnalyticsData =
  | ArticleDetail
  | RelatedArticleCard
  | SearchArticleItem
  | RoomArticleItem
  | FeedArticleItem;

type ArticleItemPayload = {
  article: ArticleAnalyticsData;
  listName?: string;
  percentage?: number;
  duration?: number;
};

/** Extract top-level Mixpanel properties from an article model. */
function getArticleTopLevelProps(article: ArticleAnalyticsData) {
  return {
    language: "language" in article ? article.language : undefined,
  };
}

type ArticleItemListPayload = {
  articles: ArticleAnalyticsData[];
  listName: string;
};

export function convertArticleToItemData(
  payload: ArticleItemPayload
): AnalyticsItemReturnData {
  const { article, listName, duration } = payload;
  return {
    item_id: article.id,
    item_name: article.title ?? "",
    item_brand: article.journal?.name ?? null,
    item_category:
      article.medical_specialties?.map((spe) => spe.uid).join(",") ?? null,
    item_category2: AnalyticsItemCategory.STANDARD,
    item_variant: AnalyticsItemVariant.ARTICLE,
    item_list_name: listName,
    audio_duration_ms:
      duration === undefined ? undefined : Math.floor(duration * 1000),
    language: article.language,
  };
}

export function gaEventSelectItemArticle(payload: ArticleItemPayload) {
  gaEventSelectItem(convertArticleToItemData(payload));
}

export function gaEventViewItemArticle(
  payload: ArticleItemPayload,
  congress: CongressAttribution | null
) {
  gaEventViewItem(convertArticleToItemData(payload), congress);
}

export function gaEventViewItemListArticle(payload: ArticleItemListPayload) {
  gaEventViewItemList(
    payload.articles.map((article) => {
      return convertArticleToItemData({
        article,
        listName: payload.listName,
      });
    })
  );
}

// The sources of a JuisciGPT answer, viewed. `engine` splits the two lists:
// the legacy Sources tab and the assistant engine's sources.
export function gaEventViewItemListGptAnswerSources(payload: {
  articles: ArticleAnalyticsData[];
  engine: ConversationEngine;
}) {
  gaPushEvent(BaseEvent.VIEW_ITEM_LIST, {
    engine: payload.engine,
    ecommerce: {
      items: payload.articles.map((article) =>
        convertArticleToItemData({
          article,
          listName: AnalyticsItemList.GPT_ANSWER_SOURCES,
        })
      ),
    },
  });
}

export function gaEventArticleConsumed(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(Event.ARTICLE_CONSUMED, {
    ...props,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticlePlayAudio(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(BaseEvent.AUDIO_PLAYED, {
    ...props,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleAudioProgress(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(BaseEvent.AUDIO_PROGRESS, {
    ...props,
    percentage: payload.percentage,
    listening_depth: payload.percentage,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleAudioComplete(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(BaseEvent.AUDIO_COMPLETE, {
    ...props,
    listening_depth: 100,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleViewOriginalPaper(payload: ArticleItemPayload) {
  gaPushEvent(BaseEvent.VIEW_ORIGINAL_PAPER, {
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleLike(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(Event.ARTICLE_LIKE, {
    ...props,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleSave(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(Event.ARTICLE_SAVE, {
    ...props,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleShare(payload: ArticleItemPayload) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(Event.ARTICLE_SHARE, {
    ...props,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}

export function gaEventArticleChatQuestion(
  payload: ArticleItemPayload & {
    question: string;
    conversationHistory: "new" | "old";
    questionNumber: number;
    // How the question reached the agent — a Chat-with-Paper template question
    // (`cwop_template`), a tapped follow-up (`followup_suggestion`) or typing
    // (`typed`). Forwarded to Mixpanel (bare `question_asked_to_a_paper`
    // mapping) so CWYOP questions are separable by input method, which the
    // event carried no way to do before.
    questionOrigin: QuestionOrigin;
    // Which engine answers: Chat with paper (`legacy`), or a new assistant
    // conversation that references the paper (`assistant`).
    engine: ConversationEngine;
  }
) {
  const props = getArticleTopLevelProps(payload.article);
  gaPushEvent(Event.ARTICLE_CHAT_QUESTION, {
    ...props,
    engine: payload.engine,
    question: payload.question,
    conversation_history: payload.conversationHistory,
    question_number: payload.questionNumber,
    question_origin: payload.questionOrigin,
    ecommerce: {
      items: [convertArticleToItemData(payload)],
    },
  });
}
